import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { del, get, post, put } from '../utils/api';
import PageLayout from '../components/common/PageLayout';

const EMPTY_FORM = {
  name: '',
  description: '',
  subject_template: '',
  body_template: '',
  send_mode: 'bcc',
  is_active: true,
};

export default function ManageEmailTemplatesPage() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [ok, setOk] = React.useState('');

  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY_FORM);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await get('/api/manage/email-templates');
      setRows(Array.isArray(res?.templates) ? res.templates : []);
    } catch (e) {
      setError(e?.message || 'Failed to load email templates');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
    setError('');
    setOk('');
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      description: row.description || '',
      subject_template: row.subject_template || '',
      body_template: row.body_template || '',
      send_mode: String(row.send_mode || 'bcc').toLowerCase() === 'individual' ? 'individual' : 'bcc',
      is_active: row.is_active !== false,
    });
    setOpen(true);
    setError('');
    setOk('');
  };

  const closeDialog = () => {
    setOpen(false);
    setEditing(null);
  };

  const onSave = async () => {
    setError('');
    setOk('');
    try {
      const payload = {
        name: String(form.name || '').trim(),
        description: String(form.description || '').trim(),
        subject_template: String(form.subject_template || '').trim(),
        body_template: String(form.body_template || '').trim(),
        send_mode: String(form.send_mode || 'bcc').trim().toLowerCase() === 'individual' ? 'individual' : 'bcc',
        is_active: !!form.is_active,
      };
      if (!payload.name || !payload.subject_template || !payload.body_template) {
        setError('Name, subject template, and body content are required.');
        return;
      }

      if (editing?.id) {
        await put(`/api/manage/email-templates/${editing.id}`, payload);
        setOk('Email template updated.');
      } else {
        await post('/api/manage/email-templates', payload);
        setOk('Email template created.');
      }
      closeDialog();
      await load();
    } catch (e) {
      setError(e?.message || 'Save failed');
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Delete template "${row.name}"?`)) return;
    setError('');
    setOk('');
    try {
      await del(`/api/manage/email-templates/${row.id}`);
      setOk('Email template deleted.');
      await load();
    } catch (e) {
      setError(e?.message || 'Delete failed');
    }
  };

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Email Templates</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage reusable email templates used by report email actions.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button>
          <Button variant="contained" onClick={openAdd}>Add Template</Button>
        </Stack>
      </Stack>

      {error ? <Typography color="error" sx={{ mb: 1.5 }}>{error}</Typography> : null}
      {ok ? <Typography color="success.main" sx={{ mb: 1.5 }}>{ok}</Typography> : null}

      <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, border: '1px solid #eee' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Delivery</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && rows.length === 0 ? (
              <TableRow><TableCell colSpan={6}>No templates found.</TableCell></TableRow>
            ) : null}
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                <TableCell>{row.description || '-'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.is_active ? 'Active' : 'Inactive'}
                    color={row.is_active ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={String(row.send_mode || 'bcc').toLowerCase() === 'individual' ? 'Individual' : 'BCC'}
                    color={String(row.send_mode || 'bcc').toLowerCase() === 'individual' ? 'secondary' : 'default'}
                  />
                </TableCell>
                <TableCell>{row.updated_at ? new Date(row.updated_at).toLocaleString() : ''}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" variant="outlined" onClick={() => openEdit(row)}>Edit</Button>
                    <Button size="small" color="error" variant="contained" onClick={() => onDelete(row)}>Delete</Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>{editing ? 'Edit Email Template' : 'Add Email Template'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Template Name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Payment Reminder"
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Subject Template"
              value={form.subject_template}
              onChange={(e) => setForm((prev) => ({ ...prev, subject_template: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Body Content"
              value={form.body_template}
              onChange={(e) => setForm((prev) => ({ ...prev, body_template: e.target.value }))}
              multiline
              minRows={10}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Delivery Mode</InputLabel>
              <Select
                label="Delivery Mode"
                value={form.send_mode || 'bcc'}
                onChange={(e) => setForm((prev) => ({ ...prev, send_mode: e.target.value }))}
              >
                <MenuItem value="bcc">BCC (single send)</MenuItem>
                <MenuItem value="individual">Individual (personalized)</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Supported placeholders: {'{{recipient_name}}'}, {'{{product_name}}'}, {'{{event_name}}'}, {'{{sponsorship_amount}}'}, {'{{payments_made}}'}, {'{{balance_amount}}'}, {'{{payment_link}}'}
            </Typography>
            <FormControlLabel
              control={(
                <Switch
                  checked={!!form.is_active}
                  onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                />
              )}
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={onSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}

